import { supabase, q } from './supabase';
import { replayRatings } from './ratings';
import { aggregateRatings, applyPeerRatings } from './peerRatings';

/**
 * Recalculates every player's rating offset and form for a group by replaying
 * its whole history, then writes the results back.
 *
 * Called after any change to a result — submitting, editing, deleting. Because
 * it starts from zero every time, correcting a game from three weeks ago fixes
 * everything downstream of it too. The old code mutated form in place, which
 * meant an edit could never be undone: form decays, so by the following month
 * no arithmetic recovers what that week should have been.
 */
export async function recomputeGroupRatings(groupId) {
  const members = await q(
    supabase.from('group_members').select('id, attributes').eq('group_id', groupId),
    'Loading the squad to recalculate ratings'
  );
  if (!members?.length) return {};

  const games = await q(
    supabase
      .from('games')
      .select('id, score_a, score_b, created_at')
      .eq('group_id', groupId)
      .eq('status', 'completed')
      .order('created_at', { ascending: true }),
    'Loading past games to recalculate ratings'
  );

  let withParticipants = [];
  if (games?.length) {
    const parts = await q(
      supabase
        .from('game_participants')
        .select('game_id, member_id, team, goals, assists, performance')
        .in('game_id', games.map((g) => g.id)),
      'Loading past results to recalculate ratings'
    );
    const byGame = {};
    (parts || []).forEach((p) => {
      (byGame[p.game_id] ||= []).push({
        memberId: p.member_id,
        team: p.team,
        goals: p.goals || 0,
        assists: p.assists || 0,
        performance: p.performance ?? null,
      });
    });
    withParticipants = games.map((g) => ({ ...g, participants: byGame[g.id] || [] }));

    // Where the group has rated someone, their average replaces the single
    // number the admin logged. Best-effort: if the table isn't there yet the
    // logged values still stand.
    const { data: peer } = await supabase
      .from('player_ratings')
      .select('game_id, subject_member_id, score')
      .in('game_id', games.map((g) => g.id));
    withParticipants = applyPeerRatings(withParticipants, aggregateRatings(peer || []));
  }

  const state = replayRatings(members, withParticipants);

  const failures = [];
  await Promise.all(
    members.map(async (m) => {
      const s = state[m.id];
      if (!s) return;
      const { error } = await supabase
        .from('group_members')
        .update({ form: s.form, rating_offset: s.offset })
        .eq('id', m.id);
      if (error) failures.push(error.message);
    })
  );

  if (failures.length) {
    throw new Error(`Ratings could not be saved: ${[...new Set(failures)].join('; ')}`);
  }
  return state;
}
